<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.report_sales.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- Filters -->
      <card class="mb-4">
        <div class="row align-items-end">
          <div class="col-md-3">
            <label class="form-control-label">{{ $t("modern_trade.report_sales.date_from") }}</label>
            <el-date-picker
              v-model="dateFrom"
              type="date"
              value-format="yyyy-MM-dd"
              class="w-100"
            ></el-date-picker>
          </div>
          <div class="col-md-3">
            <label class="form-control-label">{{ $t("modern_trade.report_sales.date_to") }}</label>
            <el-date-picker
              v-model="dateTo"
              type="date"
              value-format="yyyy-MM-dd"
              class="w-100"
            ></el-date-picker>
          </div>
          <div class="col-md-3">
            <label class="form-control-label">{{ $t("modern_trade.report_sales.company") }}</label>
            <el-select v-model="companyFilter" :placeholder="$t('all') || 'All'" clearable class="w-100">
              <el-option
                v-for="c in companies"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              ></el-option>
            </el-select>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.report_sales.group_by") }}</label>
            <el-select v-model="groupBy" class="w-100">
              <el-option value="company" :label="$t('modern_trade.report_sales.group_by_company')"></el-option>
              <el-option value="product" :label="$t('modern_trade.report_sales.group_by_product')"></el-option>
              <el-option value="invoice" :label="$t('modern_trade.report_sales.group_by_invoice')"></el-option>
            </el-select>
          </div>
          <div class="col-md-1 text-right">
            <base-button size="sm" type="primary" @click="loadData">
              <i class="fa fa-search"></i>
            </base-button>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-12 text-right">
            <base-button size="sm" type="success" :loading="exporting" @click="exportXlsx">
              <i class="fa fa-file-excel"></i> {{ $t("modern_trade.report_sales.export_xlsx") }}
            </base-button>
          </div>
        </div>
      </card>

      <!-- Summary Cards (Overall) -->
      <div class="row" v-loading="loading">
        <div class="col-xl-3 col-md-6">
          <stats-card
            :title="$t('modern_trade.report_sales.sub_total')"
            type="gradient-info"
            :sub-title="formatMoney(overall.sub_total)"
            icon="ni ni-chart-bar-32"
          >
            <template slot="footer">
              <span class="text-nowrap">{{ $t("modern_trade.report_sales.overall") }}</span>
            </template>
          </stats-card>
        </div>
        <div class="col-xl-3 col-md-6">
          <stats-card
            :title="$t('modern_trade.report_sales.vat_amount')"
            type="gradient-orange"
            :sub-title="formatMoney(overall.vat_amount)"
            icon="ni ni-money-coins"
          >
          </stats-card>
        </div>
        <div class="col-xl-3 col-md-6">
          <stats-card
            :title="$t('modern_trade.report_sales.net_amount')"
            type="gradient-green"
            :sub-title="formatMoney(overall.net_amount)"
            icon="ni ni-chart-pie-35"
          >
          </stats-card>
        </div>
        <div class="col-xl-3 col-md-6">
          <stats-card
            :title="$t('modern_trade.report_sales.paid_amount')"
            type="gradient-success"
            :sub-title="formatMoney(overall.paid_amount)"
            icon="ni ni-credit-card"
          >
            <template slot="footer">
              <span class="text-nowrap text-danger">
                {{ $t("modern_trade.report_sales.outstanding") }}: {{ formatMoney(overall.outstanding) }}
              </span>
            </template>
          </stats-card>
        </div>
      </div>

      <!-- Breakdown table -->
      <card class="no-border-card" body-classes="px-0 pb-1">
        <template slot="header">
          <h5 class="h3 mb-0">{{ $t("modern_trade.report_sales.title") }}</h5>
        </template>
        <div v-loading="loading">
          <el-table :data="breakdownRows" row-key="key" header-row-class-name="thead-light">
            <el-table-column :label="groupLabel" min-width="180">
              <template slot-scope="{ row }">{{ row.name || row.company_name || row.product_name || row.doc_no || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.report_sales.invoice_count')" min-width="100" align="center">
              <template slot-scope="{ row }">{{ row.invoice_count !== undefined ? row.invoice_count : "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.report_sales.sub_total')" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatMoney(row.sub_total) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.report_sales.vat_amount')" min-width="120" align="right">
              <template slot-scope="{ row }">{{ formatMoney(row.vat_amount) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.report_sales.net_amount')" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatMoney(row.net_amount) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.report_sales.paid_amount')" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatMoney(row.paid_amount) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.report_sales.outstanding')" min-width="130" align="right">
              <template slot-scope="{ row }">
                <span :class="{ 'text-danger': Number(row.outstanding) > 0 }">{{ formatMoney(row.outstanding) }}</span>
              </template>
            </el-table-column>
          </el-table>
          <p v-if="!loading && breakdownRows.length === 0" class="text-muted text-center py-4 mb-0">
            {{ $t("modern_trade.report_sales.no_data") }}
          </p>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, DatePicker } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { StatsCard } from "@/components";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtSalesReport",
  components: {
    RouteBreadCrumb,
    StatsCard,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
    }),
    groupLabel() {
      switch (this.groupBy) {
        case "company": return this.$t("modern_trade.report_sales.company");
        case "product": return this.$t("modern_trade.sale_order.product");
        case "invoice": return this.$t("modern_trade.invoice.doc_no");
        default: return "-";
      }
    },
  },
  data() {
    return {
      loading: false,
      exporting: false,
      companies: [],
      dateFrom: null,
      dateTo: null,
      companyFilter: null,
      groupBy: "company",
      overall: {
        sub_total: 0,
        vat_amount: 0,
        net_amount: 0,
        paid_amount: 0,
        outstanding: 0,
      },
      breakdownRows: [],
    };
  },
  mounted() {
    this.setDefaultDateRange();
    this.loadCompanies();
    this.loadData();
  },
  methods: {
    setDefaultDateRange() {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const pad = n => String(n).padStart(2, "0");
      const toStr = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      this.dateFrom = toStr(firstDay);
      this.dateTo = toStr(lastDay);
    },
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    buildParams() {
      const params = { date_from: this.dateFrom, date_to: this.dateTo, group_by: this.groupBy };
      if (this.companyFilter) params.company_id = this.companyFilter;
      return params;
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await ModernTradeService.reportSales(this.buildParams());
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.overall = Object.assign(
          { sub_total: 0, vat_amount: 0, net_amount: 0, paid_amount: 0, outstanding: 0 },
          d.overall || {}
        );
        const rows = d.breakdown || d.items || [];
        this.breakdownRows = rows.map((r, idx) => Object.assign({ key: r.id || r.company_id || r.product_id || r.doc_no || idx }, r));
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    async exportXlsx() {
      this.exporting = true;
      try {
        const res = await ModernTradeService.reportSalesExport(this.buildParams());
        const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mt_sales_report_${this.dateFrom}_${this.dateTo}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.exporting = false;
      }
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    errMsg(err) {
      return (err.response && err.response.data && err.response.data.Cause) || err.message;
    },
  },
};
</script>
